import { useRef } from 'react';

import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';

import { useMediaQuery } from 'hooks/browser/mediaQueries';
import { useClickOutside } from 'hooks/browser/outerClick';
import { useGetSearchTerm, useToggleSearch } from 'hooks/search';

import { CloseIcon } from 'components/icons/CloseIcon';
import { TextField } from 'components/shared/forms/TextField';

import { SearchGradientIcon } from './icons/SearchGradientIcon';

interface IProps {
  defaultTerm?: string;
  placeholder?: string;
  includeMobile?: boolean;
  onSearch: (searchTerm: string) => void;
}

export const SearchForm = ({
  defaultTerm,
  placeholder,
  includeMobile = false,
  onSearch,
}: IProps) => {
  const { term } = useGetSearchTerm();
  const { isActive, setInactive } = useToggleSearch();

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => {
    setInactive();
  });

  const handleChange = ({ term }: { term: string }) => {
    onSearch(term);
  };

  const { matches: isDesktop } = useMediaQuery('(min-width: 640px)');

  return (
    <Formik initialValues={{ term }} onSubmit={handleChange}>
      {({ setFieldValue }) => (
        <AnimatePresence>
          {(isDesktop || isActive || !includeMobile) && (
            <motion.div
              className={classNames(
                'flex items-center bg-culturedPearlGrey dark:bg-mineShaftGrey',
                {
                  'absolute inset-0 z-10 w-full px-3':
                    !isDesktop && includeMobile,
                },
              )}
              transition={{ duration: isDesktop ? 0 : 0.2 }}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: -20 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Form
                className="flex flex-grow sm:relative sm:w-[300px]"
                autoComplete="off"
              >
                <div ref={ref} className="relative w-full">
                  <TextField
                    className="w-full pl-8 text-[13px]"
                    name="term"
                    placeholder={placeholder}
                    variant="medium"
                  />

                  <button
                    className="absolute left-[10px] top-[50%] -translate-y-[50%]"
                    type="submit"
                  >
                    <SearchGradientIcon />
                  </button>

                  <button
                    className="absolute right-[10px] top-[50%] -translate-y-[50%]"
                    type="button"
                    onClick={() => {
                      handleChange({ term: '' });
                      setFieldValue('term', '');
                    }}
                  >
                    <CloseIcon
                      backgroundColor="transparent"
                      color="currentColor"
                      size={24}
                    />
                  </button>
                </div>

                {!isDesktop && includeMobile && (
                  <button
                    onClick={setInactive}
                    className="ml-2 p-2 text-[13px]"
                  >
                    Close
                  </button>
                )}
              </Form>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </Formik>
  );
};
